import {addOrReplaceAndRunAnalysisStep, updateAndRunAnalysisStep} from 'analysis-tool/AnalysisActions';
import {
  SINGLE_DATASET_INPUT_STEP_DATA_NAME,
  SINGLE_DATASET_OUTPUT_STEP_DATA_NAME,
} from 'analysis-tool/AnalysisStepDataNames';
import {AnalysisGraphQL, GetAnalysisPipelineStep} from 'analysis-tool/AnalysisTypes';
import {findAndAssertSingleStepDataFrameId} from 'analysis-tool/data/findAndAssertStepDataFrameIds';
import getStepFromAnalysis from 'analysis-tool/data/getStepFromAnalysis';
import {DataReference} from 'analysis-tool/ui/main-view/MainViewTypes';
import {DataFrameHideColumnOptions} from 'data-frames/header-menu/DataFrameHeaderMenuOptions';
import {PipelineStepType} from 'graphql-client/__generated/graphqlGlobalTypes';
import {withLoadingScreen} from 'util/Promises';

const LOADING_MESSAGE = 'Hiding column';

async function updateHideColumnStep(
  analysis: AnalysisGraphQL,
  hideColumnStep: GetAnalysisPipelineStep,
  systemName: string
): Promise<void> {
  const previousSystemNameVar = hideColumnStep?.config?.variables.find(
    (variable) => variable.name === 'columns'
  );
  await updateAndRunAnalysisStep(analysis.id, {
    stepId: hideColumnStep.id,
    stepConfig: {columns: [...(previousSystemNameVar?.value || []), systemName]},
  });
}

async function addHideColumnStep(
  analysis: AnalysisGraphQL,
  columnSystemName: string,
  inputDataReference: DataReference,
  inputDataFrameId?: string,
  outputDataReferences?: Array<DataReference>
): Promise<void> {
  await addOrReplaceAndRunAnalysisStep(analysis.id, {
    stepType: PipelineStepType.HIDE_COLUMNS,
    stepVersion: 0,
    stepConfig: {columns: [columnSystemName], columnsToKeep: []},
    stepInputParents: {[SINGLE_DATASET_INPUT_STEP_DATA_NAME]: [inputDataReference]},
    ...(inputDataFrameId == null ? {} : {stepInput: {inputDataFrameId}}),
    ...(outputDataReferences == null
      ? {}
      : {stepOutputChildren: {[SINGLE_DATASET_OUTPUT_STEP_DATA_NAME]: outputDataReferences}}),
  });
}

export function getHideColumnOptions(
  analysis: AnalysisGraphQL,
  step: GetAnalysisPipelineStep,
  inputDataReference: DataReference,
  inputDataFrameId?: string,
  outputDataReferences?: Array<DataReference>
): DataFrameHideColumnOptions {
  const inputStep = getStepFromAnalysis(analysis, inputDataReference.stepId);
  inputDataFrameId =
    inputDataFrameId ?? findAndAssertSingleStepDataFrameId(inputStep, inputDataReference.stepDataName);
  const inputDataFrame = inputStep.outputStepDatas
    .find((stepData) => stepData.name === inputDataReference.stepDataName)!
    .dataFrames.find((dataFrame) => dataFrame.id === inputDataFrameId)!;
  const disabledMessage =
    inputDataFrame?.allColumnsJson.length <= 1 ? 'Cannot hide the only column in the table' : undefined;

  if (step.definition.stepType === PipelineStepType.HIDE_COLUMNS) {
    const hideColumn = async (columnSystemName: string): Promise<void> => {
      await withLoadingScreen(updateHideColumnStep(analysis, step, columnSystemName), LOADING_MESSAGE);
    };
    return {hideColumn, disabledMessage};
  }
  // Previous step in chain is not a hide column step, so we make an new transform
  const hideColumn = async (columnSystemName: string): Promise<void> => {
    await withLoadingScreen(
      addHideColumnStep(
        analysis,
        columnSystemName,
        inputDataReference,
        inputDataFrameId,
        outputDataReferences
      ),
      LOADING_MESSAGE
    );
  };
  return {hideColumn, disabledMessage};
}
